import { Component, OnInit, Input, Output, EventEmitter, 
  ViewChild, Optional, Inject, OnDestroy } from '@angular/core';
import { FormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { AccountService } from 'src/app/services/account.service';

import momentTimezones from 'moment-timezone';
import moment from 'moment';
import { PaymentService } from '../services/payment.service';
import { SubscriptionService } from '../services/subscription.service';
import { AccountPaymentComponent } from '../account-payment/account-payment.component';
import { AlertController } from '@ionic/angular';
import { environment } from 'src/environments/environment';
import { PaymentCalculatorService } from '../services/payment-calculator.service';
import { ToggleService } from '../services/toggle.service';
import { ModalController } from '@ionic/angular';
import { LanguageSwitcherPopupComponent } from '../language-switcher/language-switcher-popup.component';
import { LanguageService } from '../services/language.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-account-settings-general',
  templateUrl: './general.component.html',
  styleUrls: ['./general.component.scss'],
})
export class AccountSettingsGeneralComponent implements OnInit, OnDestroy {
  @Output() pending = new EventEmitter<Object>();
  @Output() buttonText = new EventEmitter<Object>();
  @Output() changeSection = new EventEmitter<String>();

  public generalAccountInfoForm: UntypedFormGroup;
  public type;
  public numberKilns;
  public expiration; 
  public isExpired = false;
  public editing = false;
  public loading = false;
  public updatePending = false;
  public monthlyCalculation;
  public annualCalculation;

  private account_id;
  public payment;
  public paymentExpiredOrNonExistent = false;
  public formLoaded = false;
  private existingSubscriptionId = false;
  public freeToPremium = this.isToggleEnabled("freeToPremium");
  public showPricingModal = false;
  
  // Language-related properties
  public currentLanguage: string = 'en';
  public currentLanguageInfo: any = { name: 'English', flag: '🇺🇸' };
  private languageSubscription: Subscription;
  
  @ViewChild('paymentsForm', { read: AccountPaymentComponent }) paymentForm;

  constructor(private accountService: AccountService, 
    private paymentService: PaymentService,
    private paymentCalculatorService: PaymentCalculatorService,
    private subscriptionService: SubscriptionService,
    public alertController: AlertController,
    private toggleService: ToggleService,
    @Optional() @Inject('accountInformation') public accountInformation: any,
    @Optional() @Inject('canAdminister') public canAdminister: boolean,
    private modalController: ModalController,
    private languageService: LanguageService) {}

  isToggleEnabled(toggleName: string, userRole?: string): boolean {
    return this.toggleService.isToggleEnabled(toggleName, userRole);
  }

  ngOnInit() {
    // Subscribe to language changes
    this.languageSubscription = this.languageService.currentLanguage$.subscribe(language => {
      this.currentLanguage = language;
      this.currentLanguageInfo = this.languageService.getCurrentLanguageInfo();
    });
    
    // Set initial language
    this.currentLanguage = this.languageService.getCurrentLanguage();
    this.currentLanguageInfo = this.languageService.getCurrentLanguageInfo();
    
    this.loading = true;  
    if(this.accountInformation) {
      this.account_id = this.accountInformation.general.external_id;
      this.type = (this.accountInformation && this.accountInformation.general) ? this.accountInformation.general.type : 'N/A';
      this.numberKilns = (this.accountInformation && this.accountInformation.general) ? this.accountInformation.general.kiln_limit : 'N/A';
      this.formLoaded = false;
      this.createForm();

      let setUpFormWithoutSubscription = () => {
        this.createForm();
        let expiration = (this.accountInformation && this.accountInformation.general) ? this.accountInformation.general.expiration : null;
        if(expiration) this.isExpired = moment(new Date()).isAfter(moment(expiration));   
        this.expiration = moment(expiration).format('YYYY-MM-DD');
        this.paymentService.getPaymentInfo(this.account_id).then((payment_response) => {
          this.payment = payment_response.payment
          this.paymentExpiredOrNonExistent = !payment_response.payment ? true : false;
        });
      }

      if(this.isToggleEnabled("freeToPremium")) {
        this.subscriptionService.get(this.account_id).then((subscription) => {
          if(subscription) this.existingSubscriptionId = subscription['id']
          this.createForm(subscription);
          let expiration = subscription ? subscription['expirationDate'] : (this.accountInformation && this.accountInformation.general) ? this.accountInformation.general.expiration : null;
          this.expiration = moment(expiration).format('YYYY-MM-DD');
          if(expiration) {
            let now = moment(new Date())
            let exp = moment(expiration)
            this.isExpired = now.isAfter(exp);   
          }
                
          this.paymentService.getPaymentInfo(this.account_id).then((payment_response) => {
            this.payment = payment_response.payment
            this.paymentExpiredOrNonExistent = !payment_response.payment ? true : false;
          });    
        }).catch((error) => {
          console.log('error getting subscription', error);
          setUpFormWithoutSubscription();
        });
      } else {
        setUpFormWithoutSubscription();
      }
    }
  }

  ngOnDestroy() {
    // Clean up subscription
    if (this.languageSubscription) {
      this.languageSubscription.unsubscribe();
    }
  }

  async successfulUpdate() {
    this.editing = false;
    this.updatePending = false;
    this.pending.emit({
      is_pending: false,
      message: 'Account general information updated.',
      success: true
    });
  }

  createForm(subscription = undefined) {
    if(!this.generalAccountInfoForm && this.accountInformation) {
      var guess_time_zone = momentTimezones.tz.guess();
      let account_has_time_zone = this.accountInformation && this.accountInformation.general && this.accountInformation.general.timezone
      let time_zone = (account_has_time_zone) ? this.accountInformation.general.timezone : guess_time_zone;
      let timezone = new UntypedFormControl(time_zone, [Validators.required]);

      let freq = 'kiln_annual'
      let diffFreq = 1;
      if(subscription) {
        freq = (subscription.planId || 'kiln_annual')      
        let difference = moment.duration(moment(subscription.paidThroughDate).diff(moment(subscription.firstBillingDate)))
        diffFreq = Math.round(freq.includes('annual') ? difference.asYears() : difference.asMonths())
      }
      
      this.generalAccountInfoForm = new UntypedFormGroup({
        type: new UntypedFormControl(this.type, [Validators.required]),
        numberControllers: new UntypedFormControl(this.numberKilns > 0 ? this.numberKilns : 1, [Validators.required, Validators.min(1)]),
        frequency: new UntypedFormControl(freq, [Validators.required]),
        renewals: new UntypedFormControl(diffFreq || 1, [Validators.required]),
        timezone: timezone,
      });
      
      this.formLoaded = true;
    }
  }

  getTopUSTimezones() {
    return momentTimezones.tz.names().filter((name) => {
      let default_tzs = [
        'America/New_York', 'America/Chicago',
        'America/Denver', 'America/Los_Angeles',
        'America/Anchorage', 'Pacific/Honolulu'
      ]

      return default_tzs.includes(name);
    });
  }

  getTimezones() {
    return momentTimezones.tz.names();
  }

  public async save() {
    try {
      this.updatePending = true;
      await this.paymentForm.save();     
      await this.accountService.updateGeneral(this.accountInformation.general.external_id, this.generalAccountInfoForm.value.timezone, this.generalAccountInfoForm.value.type)        
      if(this.paymentService.getNonce()) {
        if(this.existingSubscriptionId) {
          await this.subscriptionService.update(this.account_id, this.existingSubscriptionId, this.generalAccountInfoForm.value)    
        } else {
          await this.subscriptionService.create(this.account_id, this.generalAccountInfoForm.value)    
        }        
      }
      this.successfulUpdate();
    } catch(e) {
      this.pending.emit({
        is_pending: false,
        message: 'Sorry, we could not update your account information at this time',
        success: false
      });
    }
  }

  public async delete() {
    const alert = await this.alertController.create({
      header: 'Delete Confirmation',
      message: 'Are you sure you want to delete your subscription?',
      buttons: [
        {
          text: 'No, Keep My Subscription',
          role: 'cancel',
          cssClass: 'secondary',
          handler: (data) => {}
        }, {
          text: 'Yes, Cancel Subscription',
          handler: () => {
            this.subscriptionService.delete(this.account_id, this.existingSubscriptionId)
          }
        }]
      });

      alert.present();
  }

  public isValid() {
    return this.generalAccountInfoForm && this.generalAccountInfoForm.touched && this.generalAccountInfoForm.valid && this.paymentForm && this.paymentForm.isValid();
  }

  public isEditingPayment() {
    return this.paymentForm ? this.paymentForm.editing() : false;
  }

  public cancelEditing() {
    this.editing = false;
    this.buttonText.emit({editButton: 'Edit'});
  }

  startEditing(forceEdit = null) {
    this.editing = this.editing ? false : true;
    this.buttonText.emit({editButton: this.editing ? 'Cancel' : 'Edit'});
    if(forceEdit) {
      this.editing = true;
    }
    
    if(this.editing && !forceEdit) {
      this.paymentForm && this.paymentForm.showPaymentForm();
    }

    this.calculatePrice();
  }

  calculatePrice(): void {
    this.loading = true;
    
    try {
      let frequency = this.generalAccountInfoForm.value.frequency.includes('annual') ? 'annual' : 'monthly';
      let numberOfKilns = this.generalAccountInfoForm.value.numberControllers;
      let billingCycles = this.generalAccountInfoForm.value.renewals;
      
      // Calculate for both frequencies to show comparison
      const calculations = this.paymentCalculatorService.calculateBothFrequencies(numberOfKilns);
      
      this.monthlyCalculation = calculations.monthly;
      this.annualCalculation = calculations.annual;
      
      // Update billing cycles for the selected frequency
      if (frequency === 'monthly') {
        this.monthlyCalculation.billingCycles = billingCycles;
        this.monthlyCalculation.totalPrice = this.monthlyCalculation.pricePerKiln * numberOfKilns * billingCycles;
      } else {
        this.annualCalculation.billingCycles = billingCycles;
        this.annualCalculation.totalPrice = this.annualCalculation.pricePerKiln * numberOfKilns * billingCycles;
      }
    } catch (error) {
      console.error('Error calculating payment:', error);
      this.monthlyCalculation = null;
      this.annualCalculation = null;
    } finally {
      this.loading = false;
    }
  }

  formatPrice(price: number): string {
    return this.paymentCalculatorService.formatPrice(price);
  }

  getSavingsPercentage(): number | null {
    const savings = this.getAnnualSavings();
    if (savings && this.monthlyCalculation) {
      const annualMonthlyCost = this.monthlyCalculation.totalPrice * 12;
      return (savings / annualMonthlyCost) * 100;
    }
    return null;
  }

  getAnnualSavings(): number | null {
    if (this.monthlyCalculation && this.annualCalculation) {
      return this.annualCalculation.totalPrice - (this.monthlyCalculation.totalPrice * 12);
    }
    return null;
  }

  showPricingInfo() {
    this.showPricingModal = true;
  }

  closePricingInfo() {
    this.showPricingModal = false;
  }

  async openLanguageSwitcher() {
    const modal = await this.modalController.create({
      component: LanguageSwitcherPopupComponent,
      componentProps: {},
      cssClass: 'language-switcher-modal'
    });
    
    // Handle modal dismissal to refresh language display
    modal.onDidDismiss().then(() => {
      // Language will be updated automatically via subscription
      // No additional action needed as the language service handles the change
    });
    
    return await modal.present();
  }
}
